import { useEffect, useState, useCallback } from "react";
import { CheckSquare, Square, Rocket, Radar, MailCheck, CircleCheck, TriangleAlert, Hand } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STATUS_STYLE = {
  pass: { icon: CircleCheck, cls: "text-emerald-400", label: "Verified" },
  attention: { icon: TriangleAlert, cls: "text-amber-300", label: "Needs action" },
  manual: { icon: Hand, cls: "text-slate-500", label: "Manual" },
};

export default function AdminLaunch() {
  const [data, setData] = useState(null);
  const [sweep, setSweep] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/launch-checklist");
      setData(data);
    } catch {
      toast.error("Failed to load checklist");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await api.get("/admin/launch-sweep");
      setSweep(data);
      toast.success("Sweep complete — review each item below");
    } catch {
      toast.error("Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      const { data } = await api.post("/admin/launch-sweep/test-email");
      setEmailSent(true);
      toast.success(`Test email sent to ${data.to} — check your inbox`);
    } catch {
      toast.error("Could not send test email");
    }
  };

  const toggle = async (key, done) => {
    try {
      const { data } = await api.patch("/admin/launch-checklist", { key, done });
      setData(data);
    } catch {
      toast.error("Update failed");
    }
  };

  if (!data) return <p className="text-slate-500 text-sm">Loading…</p>;
  const pct = Math.round((data.done / data.total) * 100);

  return (
    <div data-testid="admin-launch" className="max-w-3xl">
      <div className="bg-charcoal border border-white/10 p-6 mb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-2">[LAUNCH READINESS // MANUAL]</p>
        <p className="text-slate-300 text-sm leading-relaxed">
          Nothing here activates anything — each item is a manual sign-off. The platform stays in
          preview/test mode until every box is checked and you explicitly approve deployment.
        </p>
        <div className="mt-5 flex items-center gap-4">
          <div className="flex-1 h-2 bg-navy overflow-hidden">
            <div className="h-full bg-safety transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-safety text-sm font-bold" data-testid="launch-progress">{data.done}/{data.total}</span>
        </div>
        <button onClick={runSweep} disabled={sweeping} data-testid="launch-sweep-btn"
          className="mt-5 border border-safety text-safety hover:bg-safety hover:text-white disabled:opacity-50 font-semibold px-6 py-3 text-sm flex items-center gap-2 transition-colors">
          <Radar size={15} className={sweeping ? "animate-spin" : ""} />
          {sweeping ? "Running diagnostics…" : sweep ? "Re-run live sweep" : "Run live sweep"}
        </button>
        {sweep && (
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-3" data-testid="sweep-summary">
            Sweep read-only · Stripe mode: {sweep.stripe_mode} · nothing was changed
          </p>
        )}
      </div>

      <div className="space-y-px border border-white/10">
        {data.items.map((item, i) => {
          const check = sweep?.checks?.[item.key];
          const st = check ? STATUS_STYLE[check.auto_status] : null;
          return (
            <div key={item.key} className={`p-5 ${item.done ? "bg-emerald-500/5" : "bg-charcoal"}`} data-testid={`launch-item-${item.key}`}>
              <button onClick={() => toggle(item.key, !item.done)} className="w-full text-left flex items-start gap-4">
                {item.done
                  ? <CheckSquare size={19} className="text-emerald-400 shrink-0 mt-0.5" />
                  : <Square size={19} className="text-slate-600 shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${item.done ? "text-slate-500 line-through" : "text-white"}`}>
                    <span className="font-mono text-[10px] text-slate-500 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {item.label}
                  </p>
                  {item.done && item.done_at && (
                    <p className="font-mono text-[10px] text-slate-600 mt-1 uppercase tracking-widest">
                      Signed off {new Date(item.done_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {st && (
                  <span className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest ${st.cls} shrink-0`}
                    data-testid={`sweep-status-${item.key}`}>
                    <st.icon size={14} /> {st.label}
                  </span>
                )}
              </button>
              {check && (
                <p className="text-slate-400 text-xs mt-2.5 ml-9 leading-relaxed" data-testid={`sweep-detail-${item.key}`}>{check.detail}</p>
              )}
              {check && item.key === "sender" && (
                <button onClick={sendTestEmail} disabled={emailSent} data-testid="sender-test-email-btn"
                  className="ml-9 mt-3 border border-white/20 hover:border-safety disabled:opacity-50 text-white text-xs font-semibold px-4 py-2.5 flex items-center gap-2 transition-colors">
                  <MailCheck size={13} /> {emailSent ? "Sent — check your inbox" : "Send me a test email"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {data.done === data.total && (
        <div className="mt-6 bg-emerald-500/10 border border-emerald-500/30 p-6" data-testid="launch-ready-banner">
          <p className="font-display font-bold text-emerald-400 text-lg">Checklist complete.</p>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">
            Deployment is still your manual call: publish from the platform, connect your domain,
            and confirm Stripe live keys. Nothing publishes or charges automatically.
          </p>
        </div>
      )}

      <div className="mt-6 bg-charcoal border border-white/10 p-6 flex items-start gap-4">
        <Rocket size={20} className="text-safety shrink-0 mt-0.5" />
        <p className="text-slate-400 text-sm leading-relaxed">
          When all {data.total} items are signed off, deployment is still a manual step: publish from the
          platform, connect your domain, and confirm Stripe live keys. Nothing publishes automatically.
        </p>
      </div>
    </div>
  );
}
